import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  MessageSquare, Clock, AlertCircle, CheckCircle, AlertTriangle,
  Send, ChevronRight, Filter, Search, Loader2, Trash2, User,
  BarChart3, TrendingUp, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account_access', label: 'Account / Access' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'general', label: 'General Question' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'waiting_for_admin', label: 'Waiting for Admin', color: 'bg-purple-100 text-purple-700', icon: MessageSquare },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
];

export default function TicketManagement() {
  const { api, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [filterStatus, filterPriority]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      
      const response = await api.get(`/tickets?${params}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/tickets/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSending(true);
      const response = await api.post(`/tickets/${selectedTicket.id}/reply`, {
        message: replyText,
        is_internal: isInternalNote
      });
      setSelectedTicket(response.data);
      setReplyText('');
      setIsInternalNote(false);
      toast.success(isInternalNote ? 'Internal note added' : 'Reply sent');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const response = await api.put(`/tickets/${selectedTicket.id}/status`, { status });
      setSelectedTicket(response.data);
      toast.success(`Status changed to ${status}`);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      const response = await api.put(`/tickets/${selectedTicket.id}/priority`, { priority });
      setSelectedTicket(response.data);
      toast.success(`Priority changed to ${priority}`);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    
    try {
      await api.delete(`/tickets/${ticketToDelete.id}`);
      toast.success('Ticket deleted');
      setShowDeleteDialog(false);
      setTicketToDelete(null);
      if (selectedTicket?.id === ticketToDelete.id) {
        setShowTicketDialog(false);
        setSelectedTicket(null);
      }
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const openTicketDetail = async (ticket) => {
    try {
      const response = await api.get(`/tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setShowTicketDialog(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const confirmDelete = (ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteDialog(true);
  };

  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  const getCategoryLabel = (category) => CATEGORIES.find(c => c.value === category)?.label || category;

  const getFilteredTickets = () => {
    let filtered = tickets;
    
    // Tab filter
    if (activeTab === 'urgent') {
      filtered = filtered.filter(t => t.priority === 'urgent' || t.priority === 'high');
    } else if (activeTab === 'open') {
      filtered = filtered.filter(t => t.status === 'open' || t.status === 'in_progress');
    } else if (activeTab === 'resolved') {
      filtered = filtered.filter(t => t.status === 'resolved' || t.status === 'closed');
    }
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(ticket =>
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.created_by_email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredTickets = getFilteredTickets();

  return (
    <div className="space-y-6" data-testid="ticket-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ticket Management</h1>
          <p className="text-slate-500 mt-1">Manage support tickets from restaurant admins</p>
        </div>
        <Button variant="outline" onClick={() => { fetchTickets(); fetchStats(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-6 h-6 mx-auto text-slate-500 mb-2" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Tickets</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-6 h-6 mx-auto text-yellow-500 mb-2" />
              <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.open || 0}</p>
              <p className="text-xs text-slate-500">Open</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-blue-600">{stats.by_status?.in_progress || 0}</p>
              <p className="text-xs text-slate-500">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold text-red-600">{stats.by_priority?.urgent || 0}</p>
              <p className="text-xs text-slate-500">Urgent</p>
            </CardContent>
          </Card>
          <Card className="border-green-200">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">
                {stats.avg_response_time_hours ? `${stats.avg_response_time_hours}h` : 'N/A'}
              </p>
              <p className="text-xs text-slate-500">Avg Response</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs & Filters */}
      <div className="flex flex-col gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Tickets</TabsTrigger>
            <TabsTrigger value="urgent" className="text-red-600">
              Urgent/High
              {tickets.filter(t => t.priority === 'urgent' || t.priority === 'high').length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {tickets.filter(t => t.priority === 'urgent' || t.priority === 'high').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="open">Active</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tickets, emails..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {PRIORITIES.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Ticket List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No tickets found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTickets.map(ticket => {
                const statusInfo = getStatusInfo(ticket.status);
                const priorityInfo = getPriorityInfo(ticket.priority);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div
                    key={ticket.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`admin-ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => openTicketDetail(ticket)}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-slate-400">{ticket.ticket_number}</span>
                          <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                          <Badge className={statusInfo.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          <Badge variant="outline">
                            {getCategoryLabel(ticket.category)}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-slate-900 truncate">{ticket.subject}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <User className="w-3 h-3" />
                          <span>{ticket.created_by_name}</span>
                          <span>•</span>
                          <span>{ticket.created_by_email}</span>
                          <span>•</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(ticket);
                          }}
                          data-testid={`delete-ticket-${ticket.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight 
                          className="w-5 h-5 text-slate-400 cursor-pointer" 
                          onClick={() => openTicketDetail(ticket)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-slate-400">{selectedTicket.ticket_number}</span>
                    <Badge variant="outline">{getCategoryLabel(selectedTicket.category)}</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => confirmDelete(selectedTicket)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <DialogTitle>{selectedTicket.subject}</DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-2">
                    <User className="w-3 h-3" />
                    <span>{selectedTicket.created_by_name} ({selectedTicket.created_by_email})</span>
                    <span>•</span>
                    <span>Created {new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                </DialogDescription>
              </DialogHeader>
              
              {/* Status & Priority Controls */}
              <div className="flex flex-wrap gap-4 py-2 border-b">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Status:</Label>
                  <Select value={selectedTicket.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-[150px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Priority:</Label>
                  <Select value={selectedTicket.priority} onValueChange={handlePriorityChange}>
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedTicket.first_response_at && (
                  <div className="text-xs text-slate-500">
                    First response: {new Date(selectedTicket.first_response_at).toLocaleString()}
                  </div>
                )}
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-[250px]">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`flex ${msg.sender_role === 'super_admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.is_internal
                        ? 'bg-yellow-50 border border-yellow-200 text-yellow-900'
                        : msg.sender_role === 'super_admin' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-slate-100 text-slate-900'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">
                          {msg.is_internal ? '🔒 Internal Note' : msg.sender_name}
                        </span>
                        <span className="text-xs opacity-70">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4 space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-yellow-700">Internal note (not visible to admin)</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Textarea
                      placeholder={isInternalNote ? "Add internal note..." : "Type your reply..."}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={2}
                      className={`flex-1 ${isInternalNote ? 'border-yellow-300 bg-yellow-50' : ''}`}
                    />
                    <Button 
                      onClick={handleSendReply} 
                      disabled={sending || !replyText.trim()}
                      className={isInternalNote ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete ticket "{ticketToDelete?.ticket_number}"? 
              This action cannot be undone and all messages will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTicket}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
